import { subtractMoney } from "../utils/money";

/**
 * Cuánto puede cobrar una línea de pago.
 *
 * El «importe a pagar» de una línea nunca supera lo que queda por cobrar, sea
 * cual sea la forma de pago y en todos los países.
 *
 * Pagar de más y recibir cambio es una operación **de caja**, no de cobro: el
 * cajero coge un billete de ₡200.000 por una venta de ₡11.655 y devuelve la
 * diferencia. Eso se anota en «Importe recibido del cliente», que es de donde
 * sale el cambio; el importe de la línea es lo que se cobra, y lo que se cobra
 * no puede pasar del pendiente. Antes el efectivo quedaba sin tope, y una línea
 * de 200.000 en una venta de 11.655 dejaba el documento cobrado de más.
 *
 * Lo que sí distingue la forma de pago es si hay cambio que dar: sólo el tipo
 * Efectivo --la clasificación `Tipo_Pago`, no el nombre--.
 */

/** El único tipo que admite recibir de más y devolver la diferencia. */
export const TIPO_EFECTIVO = "Efectivo";

/** Si con esta forma de pago se puede recibir de más y devolver cambio. */
export const permiteCambio = (tipoDePago) => tipoDePago === TIPO_EFECTIVO;

/**
 * El tope de una línea, o `null` si no hay ninguno que aplicar.
 *
 * El tope es el **pendiente**, no el total del documento: con pago mixto, lo
 * que esta línea puede cobrar es lo que queda por cobrar. Al editar una línea,
 * quien llama le suma al pendiente el importe de la propia línea.
 *
 * Devuelve `null` --y no un número grande-- sólo cuando el pendiente no se
 * conoce: un `null` obliga a quien lo use a decidir qué hacer, mientras que un
 * tope inventado se aplicaría en silencio. Un pendiente de cero o negativo es un
 * tope de cero: no queda nada por cobrar.
 *
 * @param {object} args
 * @param {number} args.pendiente   Lo que queda por cobrar del documento.
 * @returns {number|null}
 */
export const topeDeLaLinea = ({ pendiente } = {}) => {
  if (pendiente === null || pendiente === undefined || Number.isNaN(Number(pendiente))) {
    return null;
  }
  return Math.max(Number(pendiente), 0);
};

/**
 * Si un importe se pasa del tope.
 *
 * Se compara con los decimales del país: sin eso, un pendiente de 11.655 y un
 * importe de 11.655 pueden diferir en la milésima y rechazar un cobro correcto.
 *
 * @returns {boolean} `false` cuando no hay tope, que es lo mismo que caber.
 */
export const excedeElTope = ({ valor, tope, decimals = 2 } = {}) => {
  if (tope === null || tope === undefined) return false;
  return subtractMoney(Number(valor) || 0, tope, decimals) > 0;
};
